import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AccountState, selectors } from '../state';
import { AccountCommand } from '../commands';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { uuidv7 } from 'uuidv7';

/**
 * TV1-2210 (S6) — the refusal table.
 *
 * Every code below is a DELIBERATE domain refusal, not a crash: the aggregate
 * (TV1-2210 S2/S3/S4) and the country adapter (TV1-2203) raise them with a
 * `TesellyBadRequestError`, and the client library surfaces them as
 * `bodyParsed.code`. Before this table the policy `console.error`d and rethrew
 * to the global handler, so a user who picked `exento` or re-submitted an
 * existing entity got the app's generic "something went wrong" toast for a
 * decision the system made ON PURPOSE and could name exactly.
 *
 * `field` is the form field the message renders under; `'form'` is the
 * form-level banner for refusals that belong to no single input.
 */
const REFUSAL_COPY: Record<string, { field: string; message: string }> = {
  BILLING_ENTITY_ALREADY_EXISTS: {
    field: 'form',
    message: 'Esta entidad de facturación ya existe. Editá la existente en lugar de volver a crearla.'
  },
  BILLING_ENTITY_INTEGRATION_IMMUTABLE: {
    field: 'invoicingIntegrationId',
    message: 'La integración de facturación no se puede cambiar: los comprobantes ya emitidos quedarían huérfanos. Deshabilitá esta entidad y creá una nueva.'
  },
  BILLING_ENTITY_ACCOUNT_CONTEXT_MISSING: {
    field: 'form',
    message: 'No se pudo determinar la cuenta. Volvé a iniciar sesión e intentá de nuevo.'
  },
  BILLING_ENTITY_ALREADY_DISABLED: {
    field: 'form',
    message: 'Esta entidad de facturación está deshabilitada y no se puede modificar.'
  },
  BILLING_ENTITY_DISABLED: {
    field: 'form',
    message: 'Esta entidad de facturación está deshabilitada y no puede emitir comprobantes.'
  },
  BILLING_ENTITY_POINT_OF_SALE_MISSING: {
    field: 'pointsOfSale',
    message: 'Seleccioná al menos un punto de venta registrado en ARCA: sin él la entidad no puede emitir comprobantes.'
  },
  BILLING_ENTITY_POINT_OF_SALE_AMBIGUOUS: {
    field: 'pointsOfSale',
    message: 'La entidad tiene varios puntos de venta y la emisión no puede elegir uno. Dejá solamente el punto de venta con el que vas a facturar.'
  },
  BILLING_ENTITY_NOT_FOUND: {
    field: 'form',
    message: 'La entidad de facturación ya no existe. Recargá la página.'
  },
  BILLING_INVOICING_INTEGRATION_NOT_FOUND: {
    field: 'invoicingIntegrationId',
    message: 'La integración de facturación seleccionada no existe.'
  },
  BILLING_INVOICING_INTEGRATION_INVALID: {
    field: 'invoicingIntegrationId',
    message: 'La integración seleccionada no puede emitir comprobantes.'
  },
  BILLING_INVOICING_INTEGRATION_ALREADY_LINKED: {
    field: 'invoicingIntegrationId',
    message: 'Esa integración de facturación ya está vinculada a otra entidad.'
  },
  // TV1-2203 shipped a LIVE behaviour change: an `exento` emisor now REFUSES to
  // invoice where it previously issued a Factura B silently. This form is where a
  // user sets that regime, so the refusal has to be nameable here. Note it does
  // NOT arrive on this submit — it is raised at ISSUANCE — so the form ALSO warns
  // inline the moment `exento` is chosen (`BillingEntityForm.tsx`); this entry
  // exists so that if it ever does reach a submit it is still readable.
  INVOICE_FISCAL_DOCUMENT_CLASS_UNMAPPABLE: {
    field: 'vatCondition',
    message: 'Con esta condición IVA no hay una clase de comprobante verificada para emitir. Teselly no adivina la clase en un documento fiscal irreversible.'
  }
};

type ClientLibraryRejection = {
  status?: number;
  bodyParsed?: { code?: string; message?: string };
};

/** The server's refusal code, when it sent one. */
const refusalOf = ( error: unknown ): { field: string; message: string } | undefined => {
  const code = ( error as ClientLibraryRejection | null | undefined )?.bodyParsed?.code;
  return code ? REFUSAL_COPY[ code ] : undefined;
};

/**
 * Billing Entity Submit Policy
 *
 * Responsibilities:
 * - React to billing form submission
 * - Validate form
 * - Execute backend command (AddBillingEntity / EditBillingEntity)
 * - Close form on success
 * - Handle errors
 */
export const createBillingEntitySubmitPolicy = (
  backend: ClientLibraryType
): Policy<AccountState, AccountCommand> => ({
  name: 'billing-entity-submit',

  shouldReact: ( prev, next ) => {
    // React when form goes from not submitting to submitting
    return (
      !prev.billing.loading.submitting &&
      next.billing.loading.submitting &&
      next.billing.formMode.type !== 'closed'
    );
  },

  onStateChange: async ({ next, ctx }) => {
    const { formMode, formData } = next.billing;

    // Validate
    // TV1-2203 — `vatCondition` is the SELLER (emisor) regime and it DECIDES the
    // comprobante class. Left unset it lands on the derivation's
    // `undefined -> responsableInscripto` fallback — the one branch in the whole
    // matrix that GUESSES, on an irreversible fiscal document. Mantine's `required`
    // only renders an asterisk and sets `aria-required`; these forms submit through
    // an `onClick` handler, not a native <form> submit, so nothing was actually
    // blocked. This is the real gate. It is checked HERE rather than folded into
    // the `canSubmit`/valid selector so the user gets a rendered field error instead
    // of a silently disabled button.
    // `isBillingFormValid` lives in `account/state/state.ts`, which TV1-2210 is
    // rebasing over, so the check is composed here rather than folded into it.
    const vatConditionMissing = !formData.vatCondition;

    if ( !selectors.isBillingFormValid( next ) || vatConditionMissing ) {
      // Set validation errors
      const errors: Record<string, string> = {};

      if ( !formData.name.trim()) {
        errors.name = 'Nombre comercial es requerido';
      }
      if ( !formData.legalName.trim()) {
        errors.legalName = 'Razón social es requerida';
      }
      if ( !formData.documentId.trim()) {
        errors.documentId = 'CUIT es requerido';
      }
      if ( vatConditionMissing ) {
        errors.vatCondition = 'Condición IVA es requerida';
      }
      if ( !formData.billingAddress.trim()) {
        errors.billingAddress = 'Dirección fiscal es requerida';
      }
      if ( !formData.invoicingIntegrationId.trim()) {
        errors.invoicingIntegrationId = 'La integración de facturación es requerida';
      }

      ctx.dispatch(
        { type: 'account/billing/form/setErrors', payload: { errors }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'account/billing/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      return;
    }

    try {
      const entityData = {
        name: formData.name.trim(),
        legalName: formData.legalName.trim(),
        documentId: formData.documentId.trim(),
        billingAddress: formData.billingAddress.trim(),
        vatCondition: formData.vatCondition,
        grossIncome: formData.grossIncome?.trim(),
        activityStartDate: formData.activityStartDate,
        // TV1-2210 (S6) — the form now HOLDS ARCA's structured record, so this is
        // a passthrough. S4's `.map( p => ({ number: Number( p ) }) )` discarded
        // `emisionTipo` / `bloqueado` / `fchBaja` on every edit round trip.
        pointsOfSale: formData.pointsOfSale
      };

      if ( formMode.type === 'add' ) {
        await backend.commands.AddBillingEntity({
          id: uuidv7(),
          body: {
            ...entityData,
            taxAgency: formData.taxAgency as 'arca',
            invoicingIntegrationId: formData.invoicingIntegrationId.trim()
          }
        });
      } else if ( formMode.type === 'edit' ) {
        // TV1-2210 (S3/S6) — the REAL edit command. This branch used to re-dispatch
        // `AddBillingEntity` on a live entity id under a comment claiming it
        // "handles both add/update"; it never did — it appended a SECOND
        // `BillingEntityAdded` to the same stream — and since S2 it hard-rejects
        // with `BILLING_ENTITY_ALREADY_EXISTS`, so editing was simply broken.
        //
        // `invoicingIntegrationId` and `taxAgency` are NOT sent: the integration is
        // IMMUTABLE (echoing a different one is rejected with
        // `BILLING_ENTITY_INTEGRATION_IMMUTABLE`; the command accepts it only to
        // answer a whole-entity round trip honestly), and `taxAgency` is not part
        // of the editable set at all.
        await backend.commands.EditBillingEntity({
          id: formMode.entityId,
          body: entityData
        });
      }

      // Success: close form and reset loading
      ctx.dispatch(
        { type: 'account/billing/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'account/billing/form/close', payload: {}},
        { source: 'api' }
      );
    } catch ( error ) {
      // Reset loading state, ALWAYS — otherwise the form wedges under its overlay.
      ctx.dispatch(
        { type: 'account/billing/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );

      // TV1-2210 (S6) — a NAMED domain refusal renders where the user can act on
      // it and does NOT rethrow: the global handler's generic failure toast would
      // be strictly less informative than the sentence we already have, and would
      // tell the user something broke when the system decided something on purpose.
      const refusal = refusalOf( error );
      if ( refusal ) {
        ctx.dispatch(
          { type: 'account/billing/form/setErrors', payload: { errors: { [ refusal.field ]: refusal.message }}},
          { source: 'api' }
        );
        return;
      }

      // Anything unnamed IS a genuine failure and keeps the existing behaviour.
      console.error( '[account] Billing entity submit error:', error );
      throw error;
    }
  }
});
